










import { Component, Prop, Vue, Emit, Watch } from 'vue-property-decorator';

import { VueTelInput } from 'vue-tel-input';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import 'vue-tel-input/dist/vue-tel-input.css';

import { PhoneNumberDetails } from '@/models/phone-number-details';
import { Constants } from '@/common/Constants';

@Component({
	components: {
		VueTelInput,
	},
})
export default class UserPhoneInput extends Vue {
	@Prop({ required: false, type: String, default: '' })
	private userPhone!: string | null;

	@Prop({ required: false, type: Boolean, default: false })
	public disabled!: boolean;

	public get allowedCountries(): string[] {
		return Constants.SUPPORTED_COUNTRY_CODES;
	}

	public mounted(): void {
		this.syncCountry(this.phoneNumber);
	}

	public setPhone(phone: string | null): void {
		this.phoneNumber = phone ?? '';
		this.syncCountry(this.phoneNumber);
	}

	@Emit('phoneChanged')
	public onInput(
		phone: string,
		phoneObject: PhoneNumberDetails & { country?: { iso2?: string } },
	): {
		phone: string;
		phoneObject: PhoneNumberDetails;
	} {
		this.syncCountry(phone);
		return { phone, phoneObject: this.revalidate(phone, phoneObject) };
	}

	// vue-tel-input picks the flag for "+" numbers from its bundled metadata, which can't
	// resolve a country for numbers it thinks are invalid, so set the flag ourselves.
	private syncCountry(phone: string | null): void {
		if (!phone?.startsWith('+')) {
			return;
		}
		const country = parsePhoneNumberFromString(phone)?.country;
		const telInput = this.$refs.telInput as (Vue & { activeCountryCode: string }) | undefined;
		if (
			telInput &&
			country &&
			this.allowedCountries.includes(country.toLowerCase()) &&
			telInput.activeCountryCode !== country
		) {
			telInput.activeCountryCode = country;
		}
	}

	// vue-tel-input bundles its own copy of libphonenumber-js metadata, which goes stale
	// (e.g. it rejects newer AU mobile ranges like +61 494...). Re-parse with our own,
	// up-to-date libphonenumber-js so validity doesn't depend on the bundled version.
	private revalidate(
		phone: string,
		phoneObject: PhoneNumberDetails & { country?: { iso2?: string } },
	): PhoneNumberDetails {
		const defaultCountry = phoneObject?.country?.iso2?.toUpperCase() as CountryCode | undefined;
		const parsed = phone?.startsWith('+')
			? parsePhoneNumberFromString(phone)
			: parsePhoneNumberFromString(phone ?? '', defaultCountry);
		if (!parsed) {
			return { ...phoneObject, valid: false };
		}

		const isAllowedCountry =
			!!parsed.country && this.allowedCountries.includes(parsed.country.toLowerCase());
		return {
			...phoneObject,
			countryCode: parsed.country,
			countryCallingCode: parsed.countryCallingCode,
			nationalNumber: parsed.nationalNumber,
			number: parsed.number,
			valid: parsed.isValid() && isAllowedCountry,
		};
	}

	public phoneNumber: string = this.userPhone ?? '';

	@Watch('userPhone')
	public onUserPhoneChanged(newPhone: string | null): void {
		this.phoneNumber = newPhone ?? '';
		this.syncCountry(this.phoneNumber);
	}
}
